import { VersionedIntegrations } from '@dadohr/shared/domainTypes/globalConfig';
import firebase from 'firebase/app';
import { usePromise } from '../../hooks/usePromise';

/**
 * Apps repo is Gen2-only — no v1/v2 version guard needed here. Any legacy
 * Gen1-style name (with `-` separator or an `integrations_` prefix) is
 * transformed to its Gen2 equivalent on the way out:
 *   - strip leading `integrations_` prefix
 *   - replace the first `-` with `_`
 *
 * Call sites can pass either a raw Gen2 name (e.g. `admin_grantAdminClaim`)
 * or a legacy Gen1 name (e.g. `admin-grantAdminClaim`, `integrations_slack-x`).
 * Both end up calling the right deployed function.
 */
const toV2Name = (fnName: string): string =>
  fnName.replace('integrations_', '').replace('-', '_');

export const callFirebaseFunction = <T, P = object>(
  fnName: string,
  data?: P,
  timeout: number = 550000, // higher than what's a CF is allowed to run
  // `integrationName` kept for signature compatibility with legacy call sites.
  // Unused in the Gen2-only apps repo.
  _integrationName?: VersionedIntegrations
) => {
  const v2Name = toV2Name(fnName);
  return firebase.functions().httpsCallable(v2Name, { timeout })(data)
    .then((res) => res.data as T);
};

/**
 * Calls a Gen2 `onRequest` (HTTP) function directly, authenticated with the
 * current user's Firebase ID token. Use this instead of `callFirebaseFunction`
 * for endpoints that must return responses larger than the callable ~32MB cap:
 * an `onRequest` can chunked-stream an arbitrarily large body (e.g. big Workday
 * RaaS extracts). `fnName` is the deployed Gen2 name (e.g. `workday_getRawWorkdayData`).
 * No client timeout — `fetch` waits for the response while the server streams.
 */
export const callFirebaseFunctionViaHttp = async <T, P = object>(
  fnName: string,
  data?: P
): Promise<T> => {
  const projectId = firebase.app().options.projectId;
  const idToken = await firebase.auth().currentUser?.getIdToken();
  const res = await fetch(
    `https://us-central1-${projectId}.cloudfunctions.net/${fnName}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
      },
      body: JSON.stringify(data ?? {})
    }
  );
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) message = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
};

const normalCache: {
  [cacheKey: string]: { expiresAt: number; v: Promise<any> };
} = {};
const hookCache: {
  [cacheKey: string]: { expiresAt: number; result: any };
} = {};
const toCacheKey = (fnName: string, data?: object) =>
  `${fnName}-${JSON.stringify(data)}`;

export const cachedCallFirebaseFunction = <T>(
  fnName: string,
  data: any = {},
  cacheExpiryTime: number = 0,
  timeout: number = 550000 // higher than what's a CF is allowed to run
) => {
  const now = Date.now();
  const cacheKey = toCacheKey(fnName, data);
  let cacheItem = normalCache[cacheKey];
  if (!cacheItem || now > cacheItem.expiresAt) {
    cacheItem = normalCache[cacheKey] = {
      expiresAt: now + cacheExpiryTime,
      v: callFirebaseFunction<T>(fnName, data, timeout)
    };
  }
  return cacheItem.v;
};

export const useFirebaseFunction = <T>(
  fnName: string,
  data?: object,
  listeners: any[] = [],
  cacheExpiryTime: number = 0,
  refreshAfterCacheHit: boolean = false,
  integrationName?: VersionedIntegrations
) => {
  return usePromise<T>((forceValue) => {
    if (cacheExpiryTime) {
      const now = Date.now();
      const cacheKey = toCacheKey(fnName, data || {});
      const cacheItem = hookCache[cacheKey];
      const call = () => {
        return callFirebaseFunction<T>(fnName, data, 550000, integrationName)
          .then((result) => {
            hookCache[cacheKey] = { expiresAt: now + cacheExpiryTime, result };
            return result;
          })
          .catch((err) => {
            if (
              (err?.code === 'not-found' || err?.status === 404) &&
              (window.location.origin.startsWith('http://localhost:3115') ||
                window.location.origin.startsWith(
                  'https://emplexp-dev-standalone.firebaseapp.com'
                ))
            ) {
              window.location.href = `https://my.dadohr.com${window.location.pathname}`;
            }
            return Promise.reject(err);
          });
      };
      if (cacheItem && now < cacheItem.expiresAt) {
        if (refreshAfterCacheHit) {
          call().then((t) => {
            const accepted = forceValue(t);
            if (!accepted) {
              // weird race-conditiony state - clear the cache just in case
              delete hookCache[cacheKey];
            } else {
              hookCache[cacheKey].result = t;
            }
          });
        }
        return Promise.resolve(cacheItem.result as T);
      }
      return call();
    } else {
      return callFirebaseFunction<T>(fnName, data, 550000, integrationName);
    }
  }, listeners);
};
